/* Gift with purchase.
 *
 * Keeps the cart honest about the promotions defined in the gift_with_purchase
 * metaobject: adds the gift when the cart qualifies, caps it at one, and takes
 * it back out when the cart stops qualifying.
 *
 * Replaces a script that polled /cart.js every two seconds and paged through
 * /products.json looking for the gift variant. This one runs when the cart
 * actually changes and is told the variant id up front.
 */
(() => {
  const T = (key, fallback, n) => (window.stlukesT || ((k, f, x) => f.replace('{n}', x)))(key, fallback, n);
  const source = document.querySelector('[data-gwp-config]');
  if (!source) return;

  let config = {};
  try {
    config = JSON.parse(source.textContent);
  } catch (e) {
    return;
  }

  const campaigns = config.campaigns || [];
  const giftOnlyTag = (config.giftOnlyTag || '').toLowerCase();

  const root = window.Shopify?.routes?.root || '/';
  const giftProductIds = new Set(campaigns.map((c) => Number(c.giftProductId)));

  // ---- product tags ----
  // /cart.js does not report tags, so they are fetched per handle. Cached for
  // the session because a shopper's cart is the same few products over and
  // over, and the old version re-fetched all of them every two seconds.
  const CACHE_KEY = 'gwp-tags';
  let tagCache = {};
  try {
    tagCache = JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}');
  } catch (e) {}

  const saveCache = () => {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(tagCache));
    } catch (e) {}
  };

  const tagsFor = async (handle) => {
    if (tagCache[handle]) return tagCache[handle];
    try {
      const response = await fetch(`${root}products/${handle}.js`);
      if (!response.ok) return [];
      const product = await response.json();
      const tags = (product.tags || []).map((t) => String(t).toLowerCase().trim());
      tagCache[handle] = tags;
      saveCache();
      return tags;
    } catch (e) {
      // A product that cannot be read simply does not qualify. Failing open
      // would hand out gifts on a network blip.
      return [];
    }
  };

  // ---- cart calls ----
  const api = async (path, payload) => {
    const response = await fetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });
    return response.ok ? response.json() : null;
  };

  // ---- announcement ----
  const say = (message) => {
    let toast = document.querySelector('[data-gwp-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'gwp-toast';
      toast.setAttribute('data-gwp-toast', '');
      // polite, not assertive: earning a gift is good news, not an alarm that
      // should cut across whatever a screen reader is already saying.
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.setAttribute('data-on', '');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => toast.removeAttribute('data-on'), 5000);
  };

  // ---- the sync ----
  let running = false;
  let queued = false;
  // The pass currently in flight, so checkout can wait for it rather than
  // skipping it. Without this a second call during a run only set `queued` and
  // returned immediately, which is fine for a background tick and useless when
  // the answer is about to decide whether an order is correct.
  let inFlight = null;

  const sync = async () => {
    if (running) {
      queued = true;
      return false;
    }
    running = true;

    // Only removals matter to the checkout gate. A gift being added is good
    // news and no reason to stop someone paying; a gift being taken back means
    // their total is not what they were looking at, and they should see that
    // before they pay rather than after.
    let tookSomethingAway = false;
    inFlight = (async () => {
    try {
      const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
      const giftLines = cart.items.filter((item) => giftProductIds.has(Number(item.product_id)));

      // Nothing in the cart and no gift left behind: no work, and no reason to
      // fetch a single product. The progress line is cleared on the way out,
      // otherwise emptying the cart leaves "1 of 2 in your cart" on screen.
      if (!cart.items.length) {
        const empty = document.querySelector('[data-gwp-progress]');
        if (empty) {
          empty.textContent = '';
          empty.hidden = true;
        }
        document.querySelector('[data-gwp-panel]')?.removeAttribute('data-earned');

        const emptySlot = document.querySelector('[data-gwp-cart]');
        if (emptySlot) {
          emptySlot.innerHTML = '';
          emptySlot.hidden = true;
          emptySlot.removeAttribute('data-earned');
        }
        return;
      }

      // Every handle once, in parallel, rather than one at a time down the cart.
      // Tags are read for the whole cart, not just the shopping, because the
      // guard below needs to recognise a gift-only line that no campaign owns.
      const shopping = cart.items.filter((item) => !giftProductIds.has(Number(item.product_id)));
      const handles = [...new Set(cart.items.map((item) => item.handle))];
      const tagsByHandle = {};
      await Promise.all(handles.map(async (handle) => {
        tagsByHandle[handle] = await tagsFor(handle);
      }));

      let changed = false;

      for (const campaign of campaigns) {
        let quantity = 0;
        let cents = 0;

        for (const item of shopping) {
          if (!(tagsByHandle[item.handle] || []).includes(campaign.tag)) continue;
          quantity += item.quantity;
          // final_line_price is what the line actually costs after any line
          // discount, which is the number a threshold should be judged on.
          cents += item.final_line_price;
        }

        const wantsQuantity = Number(campaign.minQuantity) > 0;
        const wantsSpend = Number(campaign.minSpendCents) > 0;

        // A campaign with neither threshold set is misconfigured. Give nothing
        // away rather than everything.
        const qualifies =
          (wantsQuantity || wantsSpend) &&
          (!wantsQuantity || quantity >= Number(campaign.minQuantity)) &&
          (!wantsSpend || cents >= Number(campaign.minSpendCents));

        const line = giftLines.find((item) => Number(item.product_id) === Number(campaign.giftProductId));

        if (qualifies && !line) {
          const added = await api('cart/add.js', {
            id: campaign.giftVariantId,
            quantity: 1,
            properties: { _gift: campaign.handle }
          });
          if (added) {
            changed = true;
            say(`${campaign.giftTitle} has been added to your cart, free.`);
          }
        } else if (qualifies && line.quantity > 1) {
          // One per order. A shopper who nudged the quantity up gets it put back.
          await api('cart/change.js', { id: line.key, quantity: 1 });
          changed = true;
          tookSomethingAway = true;
          say(T('gift_one', 'The gift is limited to one per order.'));
        } else if (!qualifies && line) {
          await api('cart/change.js', { id: line.key, quantity: 0 });
          changed = true;
          tookSomethingAway = true;

          // Three different situations end up here and they are not the same
          // thing to be told. Someone who took the gift on its own never had a
          // qualifying cart. Someone one item short wants to know that, and how
          // to fix it. Only the rest get the flat explanation.
          const short = Number(campaign.minQuantity) - quantity;
          if (quantity === 0) {
            say(`${campaign.giftTitle} is a free gift and cannot be bought on its own.`);
          } else if (short > 0 && Number(campaign.minSpendCents) === 0) {
            const item = short === 1 ? 'product' : 'products';
            say(`Your free gift was removed. Add ${short} more ${campaign.tagLabel || campaign.tag} ${item} to get it back.`);
          } else {
            say('Your free gift was removed, because the cart no longer qualifies.');
          }
        }
      }

      // How this cart stands against each campaign, counted once and used by
      // both the product page and the cart drawer.
      const standing = campaigns.map((campaign) => {
        let have = 0;
        let spent = 0;
        for (const item of shopping) {
          if (!(tagsByHandle[item.handle] || []).includes(campaign.tag)) continue;
          have += item.quantity;
          spent += item.final_line_price;
        }
        const need = Number(campaign.minQuantity);
        const needCents = Number(campaign.minSpendCents);
        const met = (need > 0 || needCents > 0) &&
          (!need || have >= need) &&
          (!needCents || spent >= needCents);
        return { campaign, have, spent, need, needCents, met };
      });

      // Tell the product page how close this cart is. "1 of 2 in your cart" is
      // the line that turns the offer into a next click.
      const panel = document.querySelector('[data-gwp-panel]');
      if (panel) {
        const state = standing.find((s) => s.campaign.handle === panel.dataset.gwpCampaign);
        const line = panel.querySelector('[data-gwp-progress]');
        if (state && line) {
          let message = '';
          if (state.met) {
            message = T('gift_in_cart', 'Your gift is in the cart');
          } else if (state.need > 0 && state.have > 0) {
            message = `${state.have} of ${state.need} in your cart. Add ${state.need - state.have} more.`;
          } else if (state.needCents > 0 && state.spent > 0) {
            message = `$${Math.ceil((state.needCents - state.spent) / 100)} more to unlock your gift`;
          }
          line.textContent = message;
          line.toggleAttribute('hidden', message === '');
          panel.toggleAttribute('data-earned', state.met);
        }
      }

      // The cart drawer. Highest intent moment there is: they are looking at
      // the subtotal deciding whether to check out, and one more product is
      // the difference between a gift and no gift.
      const slot = document.querySelector('[data-gwp-cart]');
      if (slot) {
        // Earned first, then the closest to being earned, so a cart in reach of
        // two promotions is nudged toward the one it can actually finish.
        const relevant = standing
          .filter((s) => s.met || s.have > 0 || s.spent > 0)
          .sort((a, b) => (b.met ? 1 : 0) - (a.met ? 1 : 0) || (b.have - b.need) - (a.have - a.need))[0];

        if (!relevant) {
          slot.hidden = true;
          slot.innerHTML = '';
        } else {
          const { campaign, have, spent, need, needCents, met } = relevant;
          const escapeHtml = (text) =>
            String(text).replace(/[&<>"']/g, (c) =>
              ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
            );

          let text;
          if (met) {
            text = `<strong>${escapeHtml(campaign.headline || 'Your free gift')}</strong> is in your cart.`;
          } else if (need > 0) {
            const short = need - have;
            const word = short === 1 ? 'product' : 'products';
            text = `Add <strong>${short} more ${escapeHtml(campaign.tagLabel || campaign.tag)} ${word}</strong> and your free gift is added.`;
          } else {
            const left = Math.ceil((needCents - spent) / 100);
            text = `Spend <strong>$${left} more</strong> on ${escapeHtml(campaign.tagLabel || campaign.tag)} and your free gift is added.`;
          }

          slot.innerHTML = `
            <p class="cartd__gwp-text">${text}</p>
            ${met ? '' : `<a class="cartd__gwp-link" href="${escapeHtml(campaign.shopUrl)}">Shop ${escapeHtml(campaign.tagLabel || campaign.tag)}</a>`}
          `;
          slot.toggleAttribute('data-earned', met);
          slot.hidden = false;
        }
      }

      // The guard. Campaign gifts are handled above; this catches everything
      // else that is not for sale — samples, testers, a gift whose campaign has
      // been switched off — by the tag the theme also hides the buy button on.
      // Hiding a button only stops the people who were not going to do it
      // anyway; this is what actually keeps a $0.00 product out of an order.
      if (giftOnlyTag) {
        for (const item of cart.items) {
          // A live campaign's gift is that campaign's business, start to finish.
          // Without this the guard ran over a line the loop above had just
          // removed — the cart came out right, but the shopper was told "this
          // is a free gift and cannot be bought on its own" when what actually
          // happened was that their cart stopped qualifying. Two removals, and
          // the wrong explanation won because it spoke last.
          if (giftProductIds.has(Number(item.product_id))) continue;
          if (!(tagsByHandle[item.handle] || []).includes(giftOnlyTag)) continue;
          await api('cart/change.js', { id: item.key, quantity: 0 });
          changed = true;
          tookSomethingAway = true;
          say(`${item.product_title} is a free gift and cannot be bought on its own.`);
        }
      }

      if (changed) {
        // Redraw through the theme's own cart rather than a second renderer, so
        // there is one description of what is in the cart.
        if (window.StLukesCart?.refresh) {
          await window.StLukesCart.refresh();
        } else if (document.querySelector('[data-cart-page]')) {
          window.location.reload();
        }
      }
    } catch (e) {
      // A failed sync leaves the cart exactly as the shopper left it.
    } finally {
      running = false;
      if (queued) {
        queued = false;
        sync();
      }
    }
    return tookSomethingAway;
    })();

    return inFlight;
  };

  // Wait for whatever is in flight, then run a fresh pass. Used at checkout,
  // where "a sync is already running" is not good enough: the answer has to
  // cover the cart as it is now.
  const syncNow = async () => {
    // The finally clears `running` before the promise resolves, so once this
    // await returns there is no pass in flight and sync() will start a real one
    // rather than bounce off the guard.
    await inFlight?.catch(() => false);
    queued = false;
    return sync();
  };

  // ---- when to run ----
  // Watching the network rather than the theme, so a cart changed by the
  // drawer, the cart page, or anything else all land here.
  //
  // The fetch patch that spots those writes now lives in
  // stlukes-checkout-gate.js and announces them as an event. It moved because
  // it was only ever on the page while a campaign was running, and the cart
  // needs watching whether or not there is a gift going.
  document.addEventListener('stlukes:cart-write', () => {
    // After the write has landed, and never from inside our own pass.
    if (!running) sync();
  });

  // ---- the checkout check ----
  //
  // The sync runs after a cart write, and it takes about 1.4 seconds to notice
  // a cart has stopped qualifying and take the gift back out. Anyone who
  // pressed minus and then Checkout inside that window reached Shopify's
  // checkout holding a free gift they had not earned. Both checkout buttons are
  // plain form submits, so nothing was stopping them.
  //
  // The submit is now held by stlukes-checkout-gate.js, which has already
  // waited for cart writes to land by the time this runs. All that is left here
  // is the question this file can answer: did a fresh pass change the cart? If
  // it did, the shopper is not sent onward, because their total is not what it
  // was a moment ago and they should see that before they pay.
  window.StLukesCheckoutChecks = window.StLukesCheckoutChecks || [];
  window.StLukesCheckoutChecks.push(async () => {
    const changed = await syncNow();
    return changed ? 'Your cart has changed. Please check it before you continue.' : null;
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync, { once: true });
  } else {
    sync();
  }
})();
